import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {TableCellNode} from '@lexical/table';
import {useIsTemplateMode} from 'plugins/enhancedHeadings/RoleContext';
import {useEffect} from 'react';

import {$convertToEnhancedTableCellNode, EnhancedTableCellNode} from './EnhancedTableCellNode';
import {TableCellPlugin} from './TableCellPlugin';
import {TableCellSelectAllPlugin} from './TableCellSelectAllPlugin';

interface EnhancedTableCellPluginProps {
  enhancedTableCells?: boolean;
}

/**
 * Enhanced Table Cell Plugin that orchestrates multiple sub-plugins
 * for different enhanced table cell functionality.
 */
export const EnhancedTableCellPlugin = ({enhancedTableCells}: EnhancedTableCellPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const isTemplateMode = useIsTemplateMode();

  useEffect(() => {
    if (enhancedTableCells === undefined) {
      return;
    }

    // Convert regular TableCellNode to EnhancedTableCellNode for header cells
    const removeTableCellTransform = editor.registerNodeTransform(TableCellNode, (node) => {
      // Only convert if it's not already an EnhancedTableCellNode
      if (!(node instanceof EnhancedTableCellNode)) {
        // Only convert header cells (th elements)
        if (node.getHeaderStyles() > 0) {
          // Check if this node already has been processed to prevent duplicates
          const existingChildren = node.getChildren();

          // Don't convert if there are already decorator nodes (indicates it was processed)
          const hasDecoratorNodes = existingChildren.some(
            (child) => child.getType() === 'TableCellDecorator'
          );

          if (!hasDecoratorNodes) {
            const enhancedNode = $convertToEnhancedTableCellNode(node);
            node.replace(enhancedNode);
          }
        }
      }
    });

    return () => {
      removeTableCellTransform();
    };
  }, [editor, enhancedTableCells, isTemplateMode]);

  // Render sub-plugins based on enhancedTableCells mode
  return enhancedTableCells ? (
    <>
      {/* Core functionality plugin */}
      <TableCellPlugin />

      {/* Keyboard shortcuts plugin */}
      <TableCellSelectAllPlugin />
    </>
  ) : null;
};
